import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase, type BlogPost } from "@/lib/supabase";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    async function fetchPost() {
      setLoading(true);
      setNotFound(false);
      try {
        const { data, error } = await supabase
          .from("blogs")
          .select("*")
          .eq("slug", slug)
          .eq("published", true)
          .single();

        if (error || !data) {
          setNotFound(true);
        } else {
          setPost(data);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    fetchPost();
  }, [slug]);

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="w-full bg-muted animate-pulse" style={{ height: 320 }} />
        <div className="container py-12 md:py-16">
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="h-4 w-24 rounded bg-muted animate-pulse" />
            <div className="h-8 w-full rounded bg-muted animate-pulse" />
            <div className="h-8 w-3/4 rounded bg-muted animate-pulse" />
            <div className="h-4 w-40 rounded bg-muted animate-pulse" />
            <div className="mt-8 space-y-3">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-4 rounded bg-muted animate-pulse" style={{ width: `${70 + (i % 3) * 10}%` }} />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────
  if (notFound || !post) {
    return (
      <Layout>
        <div className="container py-24 text-center">
          <h1 className="text-3xl font-bold text-foreground">Post Not Found</h1>
          <p className="mt-4 text-muted-foreground">The blog post you're looking for doesn't exist or isn't published.</p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  const imageUrl = post.featured_image?.startsWith("http")
    ? post.featured_image
    : `https://transparencybadge.com${post.featured_image}`;

  const readTime = (() => {
    const words = post.content?.trim().split(/\s+/).length || 0;
    return `${Math.max(1, Math.ceil(words / 200))} min read`;
  })();

  return (
    <Layout>
      <Helmet>
        <title>{post.seo_title || post.title}</title>
        <meta name="description" content={post.seo_description || post.excerpt} />
        <link rel="canonical" href={`https://transparencybadge.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.seo_title || post.title} />
        <meta property="og:description" content={post.seo_description || post.excerpt} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={`https://transparencybadge.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "image": imageUrl,
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "author": { "@type": "Organization", "name": post.author || "Transparency Badge" },
          "publisher": {
            "@type": "Organization",
            "name": "Transparency Badge",
            "logo": { "@type": "ImageObject", "url": "https://transparencybadge.com/favicon-96x96.png" }
          },
          "mainEntityOfPage": `https://transparencybadge.com/blog/${post.slug}`
        })}</script>
      </Helmet>

      <article>
        {/* Featured Image */}
        {post.featured_image && (
          <div className="w-full bg-muted">
            <div className="container">
              <div className="mx-auto max-w-4xl overflow-hidden rounded-b-lg">
                <img
                  src={imageUrl}
                  alt={post.title}
                  className="w-full h-auto object-contain rounded-xl"
                />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="container py-12 md:py-16">
          <div className="mx-auto max-w-3xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> Back to Blog
            </Link>

            <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
              {post.category}
            </span>

            <h1 className="mt-4 text-3xl font-bold text-foreground md:text-4xl text-balance">
              {post.title}
            </h1>

            <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(post.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {readTime}
              </span>
            </div>

            <div className="prose-custom mt-10">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children }) => (
                    <h2 className="mt-10 mb-4 text-2xl font-bold text-foreground">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="mt-8 mb-3 text-xl font-semibold text-foreground">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="mb-4 leading-relaxed text-muted-foreground">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="mb-4 ml-6 list-disc space-y-2 text-muted-foreground">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="mb-4 ml-6 list-decimal space-y-2 text-muted-foreground">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="leading-relaxed">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-foreground">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="my-6 border-l-4 border-primary pl-4 italic text-muted-foreground">
                      {children}
                    </blockquote>
                  ),
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 hover:text-primary/80"
                    >
                      {children}
                    </a>
                  ),
                  table: ({ children }) => (
                    <div className="my-6 w-full overflow-x-auto rounded-lg border border-border">
                      <table className="w-full min-w-[480px] border-collapse text-sm">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-muted/50">{children}</thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody>{children}</tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="border-b border-border last:border-0">{children}</tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-4 py-3 text-left font-semibold text-foreground whitespace-nowrap">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-4 py-3 align-top text-muted-foreground">{children}</td>
                  ),
                  code: ({ className, children }) => {
                    const isBlock = /language-/.test(className || "");
                    if (isBlock) {
                      return <code className={className}>{children}</code>;
                    }
                    return (
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="my-6 w-full overflow-x-auto rounded-lg border border-border bg-[#0f1117] p-4 font-mono text-sm leading-relaxed text-slate-200">
                      {children}
                    </pre>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
